import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import {
  generateDepositLink,
  simulateStripeWebhookPaid,
  reconcileDepositHolds,
} from '@/domain/depositFlow';
import { reconcileWaiverDispatch } from '@/domain/waiverFlow';
import { reconcileTopupSchedule } from '@/domain/topupSchedulerFlow';
import type { Channel, ConversationEntry } from '@/domain/types';

// Per-customer "sequence clock": a 3-attempt call/message cadence that ends in a
// red terminal state when a customer is never reached, UNLESS they are in the
// "money cohort" (deposit on file with no booking yet); those never go red and
// re-arm at stage 3 instead of advancing to red.
//
// Follows the existing domain flow pattern: "now" is always read from the
// simulation store's clockIso (never Date.now()), and every state change goes
// through brain-store actions.

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export type SequenceStage = 1 | 2 | 3 | 'red';
export type RedKind = 'said-no' | 'never-reached';

/** Lane a queued customer sorts into. Pushed leads always enter as 'fresh'. */
export type SequenceCohort = 'fresh' | 'money' | 'open-business';

export interface SequenceState {
  stage: SequenceStage;
  redKind?: RedKind;
  lastAttemptAt?: string; // ISO
  nextDueAt?: string; // ISO - while in the future the item is "dormant"
  /** Lane this queued customer belongs to. Set when a lead is pushed to the queue. */
  cohort?: SequenceCohort;
  /**
   * The conversation this record was pushed from. Its presence marks a record as
   * "manually pushed", which lets buildStandardQueue mint a pushed-lead card even
   * when the customer has no other queue signal. Keyed by customerId, since a
   * conversation is identified by its customer in this prototype.
   */
  sourceConversationId?: string;
  /** Optional callback time chosen at push. Pins the card at its slot + rails it. */
  pinnedCallbackAt?: string; // ISO
  /** One-line description captured at push; travels onto the pushed-lead card. */
  description?: string;
  /**
   * Deposit this pushed record is about (Prompt I1): set when an expired link on
   * a now-taken date is routed back to the queue, so the resend context + agent
   * attribution travel onto the money-cohort card.
   */
  depositId?: string;
}

function nowIso() {
  return useSimulationStore.getState().clockIso;
}

/**
 * The "money cohort never goes red" predicate. A customer is in the money cohort
 * when they have a deposit that has NOT converted to a booking: either a paid
 * deposit with no bookingId, or a deposit still awaiting payment (pending). These
 * customers keep cycling at stage 3 rather than terminating red.
 */
export function isMoneyCohortCustomer(customerId: string): boolean {
  const { deposits } = useBrainStore.getState();
  return Object.values(deposits).some((deposit) => {
    if (deposit.customerId !== customerId) return false;
    if (deposit.status === 'paid' && !deposit.bookingId) return true;
    if (deposit.status === 'pending') return true;
    return false;
  });
}

/**
 * The customer was just contacted (call completed or message sent). Stamps the
 * attempt time and arms the 24h window. Stage advances on the clock passing that
 * window (see reconcileSequenceClock), not here. No-op once the sequence is red.
 */
export function recordContactAttempt(customerId: string): void {
  const brain = useBrainStore.getState();
  const existing = brain.sequenceState[customerId];
  if (existing?.stage === 'red') return;
  const now = nowIso();
  const nextDueAt = new Date(new Date(now).getTime() + TWENTY_FOUR_HOURS_MS).toISOString();
  if (!existing) {
    brain.setSequenceState(customerId, { stage: 1, lastAttemptAt: now, nextDueAt });
    return;
  }
  brain.patchSequenceState(customerId, { lastAttemptAt: now, nextDueAt });
}

/**
 * The customer replied. Reset the sequence to a fresh stage 1, clearing any red
 * state, timers and stamps, but PRESERVING push metadata (cohort,
 * sourceConversationId, description, pinnedCallbackAt) so a pushed lead survives
 * the reset. No-op for a customer with no sequence record — only queued people
 * get reordered on reply. The queue orders by priority, so "jumps to the top of
 * active" is expressed there; here we only reset the sequence.
 */
export function recordInboundReply(customerId: string): void {
  const brain = useBrainStore.getState();
  if (!brain.sequenceState[customerId]) return;
  brain.patchSequenceState(customerId, {
    stage: 1,
    redKind: undefined,
    lastAttemptAt: undefined,
    nextDueAt: undefined,
  });
}

export interface PushToQueueInput {
  customerId: string;
  /** One-line, editable at push; stored on the sequence record. */
  description: string;
  /** Captured phone (may be blank). Persisted to the customer only if they had none. */
  phone?: string;
  /** Optional callback time (ISO) chosen at push. */
  pinnedCallbackAt?: string;
}

/**
 * Hand a conversation lead to a booking agent by writing a stage-1, fresh-cohort
 * sequence record. The queue is derived, so this record is all it takes for a
 * pushed-lead card to appear in MyQ (see buildStandardQueue). No-op if the
 * customer already has a sequence record (the duplicate guard) so a second push
 * can never overwrite an in-flight sequence. If a phone was captured and the
 * customer had none on file, persist it so the pushed card can offer Call.
 */
export function pushToQueue(input: PushToQueueInput): boolean {
  const brain = useBrainStore.getState();
  if (brain.sequenceState[input.customerId]) return false;
  const customer = brain.customers[input.customerId];
  const phone = input.phone?.trim();
  if (phone && customer && !customer.phone) {
    brain.upsertCustomer({ ...customer, phone });
  }
  const description = input.description.trim();
  brain.setSequenceState(input.customerId, {
    stage: 1,
    cohort: 'fresh',
    sourceConversationId: input.customerId,
    description: description || undefined,
    pinnedCallbackAt: input.pinnedCallbackAt,
  });
  const name = customer ? `${customer.firstName} ${customer.lastName}` : 'Lead';
  brain.appendActivity({
    id: `act-push-${input.customerId}-${Date.now()}`,
    type: 'pushed-to-queue',
    customerId: input.customerId,
    body: `${name} pushed to the booking queue.${description ? ` ${description}` : ''}`,
    timestamp: nowIso(),
  });
  eventBus.publish('sequence:pushed', { customerId: input.customerId });
  return true;
}

/**
 * Advance every sequence whose 24h window has elapsed. Stages 1 and 2 step up one
 * and re-activate. Stage 3 re-activates and cycles when the customer is in the
 * money cohort; otherwise it terminates red ('never-reached').
 */
export function reconcileSequenceClock(): void {
  const brain = useBrainStore.getState();
  const nowMs = new Date(nowIso()).getTime();
  for (const [customerId, state] of Object.entries(brain.sequenceState)) {
    if (state.stage === 'red') continue;
    if (!state.nextDueAt) continue;
    if (new Date(state.nextDueAt).getTime() > nowMs) continue;
    if (state.stage === 1 || state.stage === 2) {
      brain.patchSequenceState(customerId, {
        stage: (state.stage + 1) as SequenceStage,
        nextDueAt: undefined,
      });
    } else if (isMoneyCohortCustomer(customerId)) {
      // Money-cohort customers never go red; at stage 3 they re-arm on the next
      // contact attempt instead of advancing to red.
      brain.patchSequenceState(customerId, { stage: 3, nextDueAt: undefined });
    } else {
      brain.patchSequenceState(customerId, {
        stage: 'red',
        redKind: 'never-reached',
        nextDueAt: undefined,
      });
    }
  }
}

/**
 * Advance the simulation clock by `hours`, then reconcile every sequence against
 * the new "now". The demo control for fast-forwarding the cadence.
 */
export function advanceSequenceClock(hours: number): void {
  const sim = useSimulationStore.getState();
  // Emit a +10:00 (studio-local) string for the advanced instant rather than a UTC
  // Z string, so the sim clock stays canonical and finance day-matching (which keys
  // off the Melbourne calendar day) never drifts for a morning-AEST time.
  const nextMs = new Date(sim.clockIso).getTime() + hours * 60 * 60 * 1000;
  const newIso = `${new Date(nextMs + 10 * 60 * 60 * 1000).toISOString().slice(0, 19)}+10:00`;
  sim.setClock(newIso);
  reconcileSequenceClock();
  reconcileDepositHolds();
  reconcileWaiverDispatch();
  reconcileTopupSchedule();
}

/** The customer said no. Terminate the sequence red ('said-no'). */
export function markSaidNo(customerId: string): void {
  useBrainStore.getState().setSequenceState(customerId, { stage: 'red', redKind: 'said-no' });
}

/**
 * Demo trigger: fake an inbound reply from the customer. Appends an inbound
 * conversation entry (reusing their most recent channel, else SMS) and resets
 * their sequence to stage 1.
 */
export function simulateInboundReply(customerId: string): void {
  const brain = useBrainStore.getState();
  const customer = brain.customers[customerId];
  const lastChannel: Channel =
    customer && customer.conversation.length > 0
      ? customer.conversation[customer.conversation.length - 1]!.channel
      : 'sms';
  const entry: ConversationEntry = {
    id: `convo-inbound-reply-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    customerId,
    channel: lastChannel,
    direction: 'in',
    body: "Sounds good, let's lock it in",
    timestamp: nowIso(),
    authorRole: 'customer',
  };
  brain.addConversationEntry(customerId, entry);
  recordInboundReply(customerId);
}

/**
 * Demo trigger: fire a scheduled callback now. `itemId` is a queue item id of the
 * form `q-frs-<signalId>` (strip that prefix; if absent, treat the whole string as
 * the signal id). Pulls the matching future-readiness signal's `scheduledFor` to
 * now so it enters the 15-min firing window (becomes callback-firing -> scheduledDue,
 * top of the active tab). The operator queue reads signals off the customer record,
 * so we replace the signal in place there via upsertCustomer, and keep the global
 * futureSignals map consistent via the existing upsertFutureSignal. No-op if the
 * signal is not found.
 */
export function fireScheduledCallback(itemId: string): void {
  const prefix = 'q-frs-';
  const signalId = itemId.startsWith(prefix) ? itemId.slice(prefix.length) : itemId;
  const brain = useBrainStore.getState();
  const now = nowIso();
  for (const customer of Object.values(brain.customers)) {
    const idx = customer.futureReadinessSignals.findIndex((s) => s.id === signalId);
    if (idx === -1) continue;
    const updated = {
      ...customer.futureReadinessSignals[idx]!,
      scheduledFor: now,
      status: 'scheduled' as const,
    };
    const nextSignals = [...customer.futureReadinessSignals];
    nextSignals[idx] = updated;
    brain.upsertCustomer({ ...customer, futureReadinessSignals: nextSignals });
    brain.upsertFutureSignal(updated);
    return;
  }
}

/**
 * Demo trigger: simulate a fresh deposit being paid for a customer so a paid,
 * unbooked deposit surfaces in the queue as a deposit-on-file (money) item.
 * Reuses depositFlow: generate a Stripe link, then fire the paid webhook with no
 * pre-picked slot, which marks the deposit paid (paidAt = now, no bookingId) ->
 * a deposit-on-file item. No-op if the customer is unknown.
 */
export function simulateDepositPaid(customerId: string): void {
  const brain = useBrainStore.getState();
  const customer = brain.customers[customerId];
  if (!customer) return;
  const artistId =
    Object.values(brain.artists).find((a) => a.studioId === customer.primaryStudioId)?.id ?? 'pat';
  const deposit = generateDepositLink({
    customerId,
    studioId: customer.primaryStudioId,
    artistId,
    amountAud: 400,
    channel: 'sms',
    sessionLength: 'half-day',
    source: 'agent-initiated-chat',
  });
  simulateStripeWebhookPaid({ depositId: deposit.id });
}

// A completed call counts as a contact attempt (callFlow.endCall publishes this
// with { customerId }). Registered at module scope so importing this module for
// its side effect wires the subscription at app start.
eventBus.subscribe<{ customerId: string }>('call:completed', (payload) => {
  recordContactAttempt(payload.customerId);
});
